import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { operationsClient } from '@/lib/api/client';
import { estimateDocTemplateEndpoints } from '@/lib/api/endpoints';
import { isApiError } from '@/lib/api/errors';

/**
 * Uploads a template logo image straight to operations (multipart); the server
 * streams it to S3 and returns the public URL to store in `branding.logoUrl`.
 * Mirrors {@link import('../settings/useUploadLogo').useUploadLogo} — same
 * multipart-header workaround so axios lets the browser set the boundary.
 */
export interface UploadedLogo {
  /** Presigned display URL (for the on-screen builder preview). */
  url: string;
  /** Stable S3 key stored on the template so the logo never expires. */
  key: string;
}

export function useUploadEstimateDocTemplateLogo() {
  return useMutation<UploadedLogo, Error, File>({
    mutationFn: async (file) => {
      const form = new FormData();
      form.append('file', file);
      const res = await operationsClient.post<{ url: string; key: string }>(
        estimateDocTemplateEndpoints.uploadLogo(),
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return { url: res.data.url, key: res.data.key };
    },
    onError: (err) => {
      const detail = isApiError(err)
        ? err.message
        : err instanceof Error && err.message
          ? err.message
          : null;
      toast.error(detail ? `Failed to upload logo: ${detail}` : 'Failed to upload logo');
    },
  });
}
